import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import SharedDialog from '../../../../shared/shared-dialog';
import { handleErrors } from '../../../../utils/errors';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { useStore } from '../../../state/zustand-store';
import { updateIpIntrusionStatus, UserKeys } from '../../../../api/users-api';
import { IP_Settings } from '../../../../../types';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface ToggleGpanelIpIntrusionDialogProps {
  open: boolean;
  handleClose: () => void;
  model: IP_Settings;
  changeLogRoles: string[];
}

export default function ToggleGpanelIpIntrusionDialog({
  open,
  handleClose,
  model,
  changeLogRoles,
}: ToggleGpanelIpIntrusionDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () => updateIpIntrusionStatus(!model.enabled, changeLogMessage),
    onSuccess: (updateIpSettings) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: model.enabled
          ? 'Disabled IP Intrusion successfully!'
          : 'Enabled IP Intrusion successfully!',
      });

      queryClient.setQueryData(
        [UserKeys.IpIntrusionSettings],
        updateIpSettings
      );
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: model.enabled
          ? 'There was an issue disabling IP Intrusion. Please refresh and try again.'
          : 'There was an issue enabling IP Intrusion. Please refresh and try again.',
      });
    },
    onSettled: () => {
      const event = model.enabled
        ? analytics.administration.settings.disable_ip_intrusion
        : analytics.administration.settings.enable_ip_intrusion;

      createEvent(event, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.UpdateIpIntrusionStatus.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={model.enabled ? 'Disable IP Intrusion' : 'Enable IP Intrusion'}
      dialogContent={
        <Box>
          <Typography>
            {model.enabled
              ? 'Do you wish to disable IP Intrusion?'
              : `Do you wish to enable IP Intrusion? If you aren't included in the IP Intrusion list, you will be locked out of the system.`}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: model.enabled ? 'Disable IP Intrusion' : 'Enable IP Intrusion',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'warning',
      }}
    />
  );
}
